import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { Box, Typography, useTheme } from '@mui/material';
import { PieValueType } from '@mui/x-charts/models/seriesType/pie';
import React from 'react';
import { ComparisonWithDefault } from '../components/ComparisonWithDefault.tsx';
import { PaperTooltip } from '../components/PaperTooltip.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { BuildingAlternative } from '../stores/SubprojectStore.ts';
import { formattedDecimal, formattedPercent } from '../utils.ts';
import { EnergyKind } from './types';
import { iconForEnergySink } from './utils.ts';

interface EnergySectionProps {
  alternative: BuildingAlternative;
  alternativeCompare?: BuildingAlternative;
  energyDemand: Record<EnergyKind, number>;
  energyOnsiteSupply?: Record<EnergyKind, number>;
  getColor: (idx: number) => string;
}

export const DecarbHubEnergySection: React.FC<EnergySectionProps> = ({
  alternative,
  alternativeCompare,
  energyDemand,
  energyOnsiteSupply,
  getColor,
}) => {
  const theme = useTheme();
  const { energySources } = useEnumHelpers();

  // Calculate total as a number first, then format for display
  const totalOnsiteNumber = energyDemand
    ? Object.entries(energyDemand).reduce((a, b) => a + b[1], 0)
    : 0;

  const totalOnsite =
    totalOnsiteNumber > 0 ? formattedDecimal(totalOnsiteNumber) : '---';

  const compareTotalNumber = alternativeCompare?.energyDemand
    ? Object.entries(alternativeCompare.energyDemand).reduce(
        (a, b) => a + b[1],
        0,
      )
    : 0;

  // Filter to only energy types with actual demand data
  const activeEnergyEntries = energyDemand
    ? Object.entries(energyDemand).filter(([, val]) => val > 0)
    : [];

  // Energy demand by technology - same pattern as cost section
  const energyBreakdown = Object.entries(
    alternative?.technologyParams || {},
  ).reduce<PieValueType[]>((a, [key, tech], oidx) => {
    // Sum all energy types for this technology
    const totalEnergy = tech.energyDemand
      ? Object.values(tech.energyDemand).reduce((sum, val) => sum + val, 0)
      : 0;

    // Use technologyCategory if available, otherwise use the key
    const label = tech.technologyCategory || key;

    a.push({
      id: oidx,
      value: totalEnergy,
      color: getColor(oidx),
      label: label,
    });
    return a;
  }, []);

  return (
    <Box sx={{ p: 4, minHeight: 200 }}>
      <Typography
        variant={'h6'}
        fontSize={'smaller'}
        fontWeight={600}
        sx={{
          mb: 4,
        }}
      >
        Energy
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {activeEnergyEntries.map(([key]) => {
            const Icon = iconForEnergySink(key as EnergyKind);
            return <Icon key={key} color={'info'} fontSize={'small'} />;
          })}
          <Typography variant={'body2'}>Energy Demand</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <ComparisonWithDefault
            value={totalOnsiteNumber}
            compare={compareTotalNumber || undefined}
            defaultElem={
              <Typography variant={'body2'}>
                {totalOnsite} MWh Needed
              </Typography>
            }
          />
        </Box>
        <Box className={'progress-bar'}>
          {energyBreakdown
            .filter((e) => e.value > 0)
            .map((item, idx) => (
              <PaperTooltip
                key={item.id}
                title={
                  <Typography
                    variant={'body1'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    {totalOnsiteNumber > 0
                      ? formattedPercent(item.value / totalOnsiteNumber)
                      : '---'}{' '}
                    from{' '}
                    {typeof item.label === 'function'
                      ? item.label('tooltip')
                      : item.label}
                  </Typography>
                }
              >
                <ProgressBar
                  pct={
                    totalOnsiteNumber > 0 ? item.value / totalOnsiteNumber : 0
                  }
                  delay={Math.random() * 75 + idx * 100}
                  color={item.color as string}
                />
              </PaperTooltip>
            ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'row',
          gap: 4,
        }}
      >
        {activeEnergyEntries.map(([key, demand]) => {
          const Icon = iconForEnergySink(key as EnergyKind);

          const onsiteSupplyForType =
            energyOnsiteSupply?.[key as EnergyKind] ?? 0;
          const totalDemandForType = demand;
          const onsitePct =
            totalDemandForType > 0
              ? Math.min(onsiteSupplyForType / totalDemandForType, 1)
              : 0;
          const externalPct = totalDemandForType > 0 ? 1 - onsitePct : 0;

          return (
            <Box
              key={key}
              sx={{
                flexGrow: 1,
                maxWidth: `calc(50% - ${theme.spacing(2)})`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Icon color={'info'} />
                  <Typography variant={'body2'}>
                    Total {energySources.find((k) => k.name === key)?.value}{' '}
                    Supply
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Box className={'progress-bar'}>
                  <PaperTooltip
                    title={
                      <Typography>
                        {formattedPercent(onsitePct)} - Onsite Energy Supply
                      </Typography>
                    }
                  >
                    <ProgressBar
                      pct={onsitePct}
                      delay={0}
                      color={theme.palette.warning.main}
                    />
                  </PaperTooltip>
                  <PaperTooltip
                    title={
                      <Typography>
                        {formattedPercent(externalPct)} External Providers
                      </Typography>
                    }
                  >
                    <ProgressBar
                      color={theme.palette.success.main}
                      pct={externalPct}
                      delay={Math.random() * 175}
                    />
                  </PaperTooltip>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  <Box
                    sx={{
                      height: '0.75rem',
                      width: '0.75rem',
                      backgroundColor: 'warning.main',
                      borderRadius: '4px',
                    }}
                  />
                  <Typography variant={'caption'}>
                    {formattedPercent(onsitePct)} From Internal Sources
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      height: '0.75rem',
                      width: '0.75rem',
                      backgroundColor: 'success.main',
                      borderRadius: '4px',
                    }}
                  />
                  <Typography variant={'caption'}>
                    {formattedPercent(externalPct)} From External Sources
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
